import { ContactKind, type RequiredFieldElement, type RequiredFieldForm } from 'types/back-office-settings';
import { t } from 'utils/getTranslations';

/** The Required fields card a section draws under its settings, by section anchor. */
export const REQUIRED_FIELD_CARDS: Partial<Record<string, RequiredFieldForm>> = {
    contacts: 'CLIENT',
    cases: 'CASE',
    timecards: 'TIMECARD',
};

/** The field column is sticky, so focus reached backwards scrolls a cell's checkbox or chip clear of it. */
export const CLEAR_STICKY_CLASS = 'scroll-ms-[266px] max-sm:scroll-ms-[162px]';

/** A card's copy is under `back-office-settings.required-fields.cards.<key>`. */
export const CARD_KEYS: Record<RequiredFieldForm, string> = { CLIENT: 'client', CASE: 'case', TIMECARD: 'timecard' };

/** The Client form's columns: Person then Company, each Client, Lead, Non-client. */
export const CONTACT_KINDS = [
    ContactKind.PERSON_CLIENT,
    ContactKind.PERSON_LEAD,
    ContactKind.PERSON_NON_CLIENT,
    ContactKind.COMPANY_CLIENT,
    ContactKind.COMPANY_LEAD,
    ContactKind.COMPANY_NON_CLIENT,
];

/** The VAT number row's tick, "Optional in the firm's own country", drawn under the row's name. */
export const VAT_TICK = 'VAT_OPTIONAL_IN_OWN_COUNTRY';
const VAT_NUMBER = 'VAT_NUMBER';

/** Each row's name is the label its form shows the field under. */
const ROW_LABELS: Record<string, string> = {
    'CLIENT:EMAIL': 'email',
    'CLIENT:MAILING_LANGUAGE': 'contacts.marketing.preferred-mailing-language',
    'CLIENT:PRIMARY_CONTACT': 'contacts.primary-contact',
    'CLIENT:RELATIONSHIP_MANAGER': 'contacts.about.relationship-manager',
    'CLIENT:REGISTRATION_NUMBER': 'reg-number',
    'CLIENT:PERSONAL_ID_CODE': 'contacts.person-id',
    'CLIENT:BUSINESS_AREAS': 'business-areas',
    'CLIENT:VAT_NUMBER': 'vat-number',
    'CLIENT:ASSISTANT': 'contacts.about.client-assistant',
    'CASE:PRACTICE_AREAS': 'matters.practice-areas',
    'CASE:DMS_PRACTICE_AREA': 'matters.practice-area-for-dms',
    'CASE:CASE_TYPE': 'matters.case-type',
    'CASE:SERVICE_AGREEMENT': 'matters.service-agreement',
    'CASE:RESPONSIBLE_LAWYER': 'matters.responsible-lawyer',
    'CASE:PRIMARY_CONTACT': 'contacts.primary-contact',
    'CASE:ASSISTANT': 'matters.case-assistant',
    'TIMECARD:PRACTICE_AREA': 'practice-area',
};

/**
 * A row's name; a field the page does not know yet shows its code. The Assistant rows take the name the firm gives
 * the field.
 */
export const rowLabel = (form: RequiredFieldForm, field: string, administratorFieldName: string) => {
    const id = ROW_LABELS[`${form}:${field}`];
    return id ? t(id, { name: administratorFieldName }) : field;
};

/** A row of a card: a field with its cells (one on the Case and Timecard forms), and on VAT number the tick. */
export interface RequiredFieldRow {
    field: string;
    cells: RequiredFieldElement[];
    tick?: RequiredFieldElement;
}

/** The field a listed entry is drawn in: the VAT tick sits in the VAT number row. */
export const rowField = (entry: RequiredFieldElement) => (entry.field === VAT_TICK ? VAT_NUMBER : entry.field);

/** A card's rows, in the order the entries are listed. */
export function requiredFieldRows(entries: RequiredFieldElement[]): RequiredFieldRow[] {
    const rows = new Map<string, RequiredFieldRow>();
    for (const entry of entries) {
        const field = rowField(entry);
        const row = rows.get(field) ?? { field, cells: [] };
        if (entry.field === VAT_TICK) row.tick = entry;
        else row.cells.push(entry);
        rows.set(field, row);
    }
    return [...rows.values()];
}
